/**
 * Attack pop-out — a floating "strike this player" banner, openable from
 * anywhere (the Attacks page roster, a player's profile, etc.) so you never have
 * to route to the Attacks page first. Mirrors the Shade herald's floating-modal
 * shape, but in the oxblood combat palette.
 *
 * You pick Show Mercy / Plunder / Cripple right in the pop-out; the result comes
 * back as a floating toast (same feedback channel as a deed result), and the
 * caller is handed the full fight via `onResult` so a page can refresh its
 * roster or keep a detailed replay panel.
 */
import { store } from '../state/store.js';
import { api, HttpError } from '../api/client.js';
import { showToast } from '../util/toast.js';
import { isEmailNotVerifiedError } from './VerifyEmailPrompt.js';
import { formatCrowns, formatBigNum } from '../util/formatBig.js';
import { statusPhrase } from '../util/statusPhrase.js';
import { escapeHtml } from '../util/escapeHtml.js';
import type { PvpOutcomeChoice, AttackPlayerResponse } from '@rex-mortis/shared';

/** Minimal target description. Only `name` is required; the rest sharpen the
 *  can-you-strike check and the header. When location/status are unknown the
 *  pop-out still opens and lets the server be the final arbiter. */
export interface AttackTarget {
  id?: string;
  name: string;
  level?: number;
  location?: string;
  status?: string;
}

export interface AttackBannerOptions {
  /** Handed the full fight result on a completed strike (win/loss/draw). */
  onResult?: (r: AttackPlayerResponse) => void;
}

const VIGOR_COST = 25;

const CHOICES: Array<{ choice: PvpOutcomeChoice; label: string; desc: string }> = [
  { choice: 'MERCY', label: 'Show Mercy', desc: 'Most XP. Foe rests 15-30 min. No plunder.' },
  { choice: 'PLUNDER', label: 'Plunder', desc: 'Steal 5-15% of carried crowns. Foe rests 30-45 min. Medium XP.' },
  { choice: 'CRIPPLE', label: 'Cripple', desc: 'Lowest XP, no plunder, but the longest Infirmary stay (~3 hours) — keeps them out of the game.' },
];

/** Why the viewer can't strike right now (empty string = clear to attack). */
function blockReason(target: AttackTarget): string {
  const c = store.get().character;
  if (!c) return 'You have no character.';
  if (c.status !== 'OKAY') return `You are ${statusPhrase(c.status)}.`;
  if (c.life <= 0) return 'You have no life left.';
  if (c.vigor < VIGOR_COST) return `Need ${VIGOR_COST} vigor (have ${c.vigor}).`;
  if (target.status && target.status !== 'OKAY') return `${target.name} is ${statusPhrase(target.status)}.`;
  if (target.location && target.location !== c.location) {
    return `You are in ${c.location}; ${target.name} is in ${target.location}.`;
  }
  return '';
}

function resultToast(r: AttackPlayerResponse): void {
  const bits: string[] = [];
  if (r.outcome === 'WIN') {
    bits.push(`Victory over ${r.defenderName} (${r.choice.toLowerCase()}).`);
    if (r.xpGained > 0) bits.push(`+${formatBigNum(r.xpGained)} XP.`);
    if (r.crownsPlundered !== '0') bits.push(`+${formatCrowns(r.crownsPlundered)} crowns plundered.`);
    if (r.renown && r.renown.total > 0) bits.push(`⚜ House Renown +${r.renown.total}.`);
    if (r.levelsGained > 0) bits.push(`🏆 +${r.levelsGained} level${r.levelsGained > 1 ? 's' : ''}!`);
    if (r.defenderHospitalUntil) {
      bits.push(`${r.defenderName} hospitalised until ${new Date(r.defenderHospitalUntil).toLocaleTimeString()}.`);
    }
    showToast(bits.join(' '), 'success', 6000);
  } else if (r.outcome === 'LOSS') {
    bits.push(`${r.defenderName} bested you.`);
    if (r.attackerHospitalUntil) {
      bits.push(`Hospitalised until ${new Date(r.attackerHospitalUntil).toLocaleTimeString()}.`);
    }
    showToast(bits.join(' '), 'danger', 6000);
  } else {
    showToast(`You drew against ${r.defenderName}. ${r.damageDealt} dealt, ${r.damageReceived} taken.`, 'warn', 5000);
  }
}

/**
 * Open the attack pop-out for a target. Returns immediately; the modal manages
 * its own lifecycle and self-removes.
 */
export function openAttackBanner(target: AttackTarget, opts: AttackBannerOptions = {}): void {
  // Idempotent — never stack two.
  document.getElementById('attack-banner')?.remove();
  document.getElementById('attack-overlay')?.remove();

  if (!document.getElementById('attack-banner-styles')) {
    const style = document.createElement('style');
    style.id = 'attack-banner-styles';
    style.textContent = STYLES;
    document.head.append(style);
  }

  const overlay = document.createElement('div');
  overlay.id = 'attack-overlay';
  overlay.className = 'attack-overlay';
  document.body.append(overlay);

  const modal = document.createElement('div');
  modal.id = 'attack-banner';
  modal.className = 'attack-banner';

  const reason = blockReason(target);
  const blocked = reason !== '';
  const metaPills = [
    target.level != null ? `<span class="pill">level ${target.level}</span>` : '',
    target.location ? `<span class="pill">${escapeHtml(target.location)}</span>` : '',
    target.status ? `<span class="pill ${target.status === 'OKAY' ? '' : 'warn'}">${escapeHtml(target.status)}</span>` : '',
  ].join('');

  modal.innerHTML = `
    <h2 class="attack-title">Strike ${escapeHtml(target.name)}</h2>
    ${metaPills ? `<div class="attack-meta">${metaPills}</div>` : ''}
    ${blocked ? `<p class="attack-reason">${escapeHtml(reason)}</p>` : ''}
    <div class="attack-choices">
      ${CHOICES.map(
        (o) => `
        <button class="btn attack-choice" data-attack="${o.choice}" ${blocked ? 'disabled' : ''}>
          <span class="attack-choice-label">${o.label}</span>
          <span class="attack-choice-desc">${o.desc}</span>
        </button>`,
      ).join('')}
    </div>
    <div class="attack-actions">
      <button class="btn ghost" id="attack-cancel">Stand down</button>
    </div>
  `;
  document.body.append(modal);

  const close = (): void => {
    overlay.remove();
    modal.remove();
  };

  overlay.addEventListener('click', close);
  modal.querySelector<HTMLButtonElement>('#attack-cancel')!.addEventListener('click', close);

  modal.querySelectorAll<HTMLButtonElement>('button[data-attack]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const choice = btn.dataset.attack as PvpOutcomeChoice;
      const all = modal.querySelectorAll<HTMLButtonElement>('button[data-attack]');
      all.forEach((b) => (b.disabled = true));
      try {
        const r = await api.attackPlayer({ defenderName: target.name, outcome: choice });
        store.set({ character: r.character });
        close();
        resultToast(r);
        opts.onResult?.(r);
      } catch (err) {
        all.forEach((b) => (b.disabled = false));
        if (isEmailNotVerifiedError(err)) {
          showToast('Verify your email before you can strike other players.', 'warn', 6000);
        } else {
          showToast(err instanceof HttpError ? err.message : 'Attack failed.', 'danger', 6000);
        }
      }
    });
  });
}

const STYLES = `
  .attack-overlay {
    position: fixed; inset: 0; z-index: 400;
    background: rgba(10, 4, 4, 0.82);
    backdrop-filter: blur(3px);
  }
  .attack-banner {
    position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: min(560px, 95vw); max-height: 95vh; overflow-y: auto; z-index: 401;
    background: var(--c-parchment); background-image: var(--parchment-texture);
    border: 2px solid var(--c-oxblood);
    box-shadow: 0 0 0 1px rgba(120, 30, 24, 0.6), 0 0 48px rgba(120, 30, 24, 0.4), 0 24px 70px rgba(0, 0, 0, 0.75);
    border-radius: var(--radius-lg);
    padding: var(--s-5); text-align: center;
  }
  .attack-title {
    font-family: var(--font-heading); text-transform: uppercase; letter-spacing: 0.06em;
    color: var(--c-oxblood); margin: 0 0 var(--s-2) 0; font-size: 1.7rem;
  }
  .attack-meta { display: flex; gap: var(--s-2); justify-content: center; flex-wrap: wrap; margin-bottom: var(--s-3); }
  .attack-reason { color: var(--c-danger); margin: 0 0 var(--s-3) 0; }
  .attack-choices { display: flex; flex-direction: column; gap: var(--s-2); margin-bottom: var(--s-4); }
  .attack-choice {
    display: flex; flex-direction: column; align-items: flex-start; gap: 2px;
    text-align: left; padding: var(--s-3) var(--s-4); width: 100%;
  }
  .attack-choice-label { font-weight: 700; font-size: 1.05rem; }
  .attack-choice-desc { font-size: 0.85rem; opacity: 0.85; font-weight: 400; white-space: normal; }
  .attack-choice:disabled { opacity: 0.5; }
  .attack-actions { display: flex; justify-content: center; }
`;
